import { useFetchContactById } from '@features/contacts/hooks/useFetchContactById'
import type { Contact } from '@features/contacts/types/contact'

import type { ImportedInvoiceExtraction } from '../types/importedInvoiceExtraction'
import { useFetchImportedInvoiceMatchingClient } from './useFetchImportedInvoiceMatchingClient'

interface UseResolveImportedInvoiceClientOptions {
  extraction: ImportedInvoiceExtraction
}

interface UseResolveImportedInvoiceClientResult {
  clientContact?: Contact
  isClientAmbiguous: boolean
  isClientToCreate: boolean
  isLoading: boolean
}

/**
 * Finds the Billy contact the form starts with:
 * - matched (`billyContactId`): that contact;
 * - new (`isNew`): `isNew` is a snapshot from extraction time, so the client is searched for (see
 *   `useFetchImportedInvoiceMatchingClient`). Found: that contact. None: the form keeps the extracted
 *   name and `isClientToCreate` is set, so approving creates the client first. Ambiguous:
 *   `isClientAmbiguous` is set and the form starts without a client;
 * - neither: no contact. The form keeps the extracted name until the BO picks a client, because
 *   creating one automatically could duplicate an existing client.
 *
 * A contact that cannot be fetched or found leaves the extracted name as well.
 */
export const useResolveImportedInvoiceClient = ({
  extraction,
}: UseResolveImportedInvoiceClientOptions): UseResolveImportedInvoiceClientResult => {
  const { counterparty, header } = extraction
  const matchedContactId = counterparty?.billyContactId
  const isNewClient = !matchedContactId && counterparty?.isNew === true

  const { contact: matchedContact, isLoading: isMatchedContactLoading } = useFetchContactById(matchedContactId, {
    retry: false,
  })
  const {
    contact: foundContact,
    isAmbiguous: isClientAmbiguous,
    isLoading: isFoundContactLoading,
  } = useFetchImportedInvoiceMatchingClient({
    isEnabled: isNewClient,
    name: header?.client?.name,
    registrationNo: header?.client?.registrationNo,
    vatNo: header?.client?.vatNo,
  })

  return {
    clientContact: matchedContact ?? foundContact,
    isClientAmbiguous,
    isClientToCreate: isNewClient && !foundContact && !isClientAmbiguous,
    isLoading: isMatchedContactLoading || isFoundContactLoading,
  }
}
